import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Shield, AlertTriangle, CheckCircle, Eye, Ban, ThumbsUp, Flag, RotateCcw } from "lucide-react";

interface ModerationLog {
  id: string;
  user_id: string;
  photo_url: string;
  moderation_result: any;
  action_taken: string;
  created_at: string;
  reviewed_by?: string;
  review_notes?: string;
  flagged_reasons?: string[];
  apple_store_compliant?: boolean;
  replacement_type?: string;
  original_url?: string;
}

interface ReportedImage {
  id: string;
  photo_url: string;
  user_id: string;
  report_count: number;
  reasons: string[];
  status: string;
}

const ContentModerationPanel = () => {
  const [moderationLogs, setModerationLogs] = useState<ModerationLog[]>([]);
  const [reportedImages, setReportedImages] = useState<ReportedImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("logs");

  useEffect(() => {
    fetchModerationData();
  }, []);

  const fetchModerationData = async () => {
    setLoading(true);
    try {
      // Fetch moderation logs directly from the table
      const { data: logs, error: logsError } = await supabase
        .from('content_moderation_logs' as any)
        .select(`
          id,
          user_id,
          photo_url,
          moderation_result,
          action_taken,
          created_at,
          reviewed_by,
          review_notes,
          flagged_reasons,
          apple_store_compliant,
          replacement_type,
          original_url
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (logsError) {
        console.error('Error fetching moderation logs:', logsError);
        setModerationLogs([]);
      } else {
        // Properly type and set the moderation logs
        const typedLogs: ModerationLog[] = (logs || []).map((log: any) => ({
          id: log.id,
          user_id: log.user_id,
          photo_url: log.photo_url,
          moderation_result: log.moderation_result,
          action_taken: log.action_taken,
          created_at: log.created_at,
          reviewed_by: log.reviewed_by,
          review_notes: log.review_notes,
          flagged_reasons: log.flagged_reasons || [],
          apple_store_compliant: log.apple_store_compliant || false,
          replacement_type: log.replacement_type,
          original_url: log.original_url
        }));
        
        setModerationLogs(typedLogs);
      }

      // Fetch reported images from user_reports table
      const { data: reports, error: reportsError } = await supabase
        .from('user_reports')
        .select('*')
        .eq('reported_content_type', 'image')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (reportsError) {
        console.error('Error fetching reports:', reportsError);
        setReportedImages([]);
      } else {
        // Transform user_reports data to match ReportedImage interface
        const transformedReports: ReportedImage[] = (reports || []).map(report => ({
          id: report.id,
          photo_url: report.reported_content_url || '',
          user_id: report.reported_id,
          report_count: 1, // Each row represents one report
          reasons: [report.reason],
          status: report.status || 'pending'
        }));
        
        setReportedImages(transformedReports);
      }

    } catch (error: any) {
      console.error('Error fetching moderation data:', error);
      toast({
        title: "Error",
        description: "Failed to load moderation data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveImage = async (imageUrl: string, logId: string) => {
    try {
      const { error } = await supabase
        .from('content_moderation_logs' as any)
        .update({
          action_taken: 'approved',
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          review_notes: 'Manually approved by admin'
        })
        .eq('id', logId);

      if (error) throw error;

      toast({
        title: "Image approved",
        description: "The image has been approved and will remain visible.",
      });

      fetchModerationData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleBlockImage = async (imageUrl: string, logId: string) => {
    try {
      // Update moderation log
      const { error: logError } = await supabase
        .from('content_moderation_logs' as any)
        .update({
          action_taken: 'blocked',
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          review_notes: 'Manually blocked by admin'
        })
        .eq('id', logId);

      if (logError) throw logError;

      // Use the new moderation function that replaces with placeholder
      const { error: moderateError } = await supabase.rpc('moderate_photo_with_placeholder', {
        photo_url: imageUrl,
        user_id_param: (await supabase.auth.getUser()).data.user?.id
      });

      if (moderateError) throw moderateError;

      toast({
        title: "Image moderated",
        description: "The image has been replaced with a placeholder.",
      });

      fetchModerationData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleRestoreImage = async (originalUrl: string, userId: string) => {
    try {
      // Use the restore function
      const { error } = await supabase.rpc('restore_moderated_photo', {
        original_photo_url: originalUrl,
        user_id_param: userId
      });

      if (error) throw error;

      toast({
        title: "Image restored",
        description: "The original image has been restored to the user's profile.",
      });

      fetchModerationData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getModerationBadge = (result: any) => {
    if (!result) return <Badge variant="secondary">No data</Badge>;

    const { adult, racy, violence } = result.details || {};
    
    if (adult === 'VERY_LIKELY' || racy === 'VERY_LIKELY' || violence === 'VERY_LIKELY') {
      return <Badge variant="destructive">High Risk</Badge>;
    }
    if (adult === 'LIKELY' || racy === 'LIKELY' || violence === 'LIKELY') {
      return <Badge variant="destructive">Medium Risk</Badge>;
    }
    if (adult === 'POSSIBLE' || racy === 'POSSIBLE') {
      return <Badge className="bg-orange-500">Low Risk</Badge>;
    }
    return <Badge variant="secondary">Safe</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading moderation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            Content Moderation Panel
          </CardTitle>
          <CardDescription>
            Review and manage content moderation across the platform
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="logs">Moderation Logs</TabsTrigger>
          <TabsTrigger value="reports">User Reports</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-4">
          <div className="space-y-4">
            {moderationLogs.map((log) => (
              <Card key={log.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      {log.photo_url.includes('moderated-content-placeholder') ? (
                        <div className="text-xs text-gray-500 text-center p-1">
                          Moderated Content
                        </div>
                      ) : (
                        <img
                          src={log.photo_url}
                          alt="Moderated content"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono">{log.user_id.slice(0, 8)}...</span>
                        {getModerationBadge(log.moderation_result)}
                        <Badge variant={log.action_taken === 'approved' ? 'default' : log.action_taken === 'blocked' ? 'destructive' : 'secondary'}>
                          {log.action_taken}
                        </Badge>
                        {log.replacement_type === 'placeholder' && (
                          <Badge variant="outline" className="text-orange-600 border-orange-600">
                            Placeholder
                          </Badge>
                        )}
                      </div>
                      
                      {log.moderation_result?.details && (
                        <div className="text-xs text-gray-600">
                          Adult: {log.moderation_result.details.adult} | 
                          Racy: {log.moderation_result.details.racy} | 
                          Violence: {log.moderation_result.details.violence}
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleString()}
                      </div>
                      
                      {log.action_taken === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApproveImage(log.photo_url, log.id)}
                            className="flex items-center gap-1"
                          >
                            <ThumbsUp className="w-3 h-3" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleBlockImage(log.photo_url, log.id)}
                            className="flex items-center gap-1"
                          >
                            <Ban className="w-3 h-3" />
                            Block
                          </Button>
                        </>
                      )}
                      
                      {log.replacement_type === 'placeholder' && log.original_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRestoreImage(log.original_url!, log.user_id)}
                          className="flex items-center gap-1 text-green-600 border-green-600 hover:bg-green-50"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Restore Original
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <div className="space-y-4">
            {reportedImages.map((report) => (
              <Card key={report.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <img
                      src={report.photo_url}
                      alt="Reported content"
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono">{report.user_id.slice(0, 8)}...</span>
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <Flag className="w-3 h-3" />
                          {report.report_count} reports
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        Reasons: {report.reasons.join(', ')}
                      </div>
                      
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleApproveImage(report.photo_url, report.id)}
                          className="flex items-center gap-1"
                        >
                          <ThumbsUp className="w-3 h-3" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleBlockImage(report.photo_url, report.id)}
                          className="flex items-center gap-1"
                        >
                          <Ban className="w-3 h-3" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {reportedImages.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No reported images pending review
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Moderation Settings</CardTitle>
              <CardDescription>Configure content moderation rules</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium">Current Settings:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Adult content: Block POSSIBLE and above</li>
                  <li>• Racy content: Block POSSIBLE and above</li>
                  <li>• Violence: Block LIKELY and above</li>
                  <li>• Manual review required for borderline cases</li>
                  <li>• User reports trigger immediate review</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContentModerationPanel;
